import { Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { CheckIcon, ExternalLinkIcon, SearchIcon } from 'lucide-react';
import { useAdminSave } from '../AdminContext';
import { useConfirmLeave, useUnsavedWork } from '../../contexts/UnsavedWorkContext';
import { adminNav, canSee, sectionFor } from '../adminNav';
import { useCan } from '../useCan';
import type { Access } from '../../core/rules/permissions';
import { btnPrimary } from './styles';
import { ADMIN_BASE, shopHref } from '../../utils/hosts';
import { LOGO } from '../../data/content';
import { PageErrorBoundary } from '../../ui/PageErrorBoundary';
import { AdminNavList } from './shell/AdminNavList';
import { AdminAccount } from './shell/AdminAccount';
import { AdminMenuSheet } from './shell/AdminMenuSheet';
import { AdminTabBar } from './shell/AdminTabBar';
import { AdminSaveBar } from './shell/AdminSaveBar';
import { useNavCounts } from './shell/useNavCounts';
import { CommandMenu } from './shell/CommandMenu';

/** Alles wat dit account mag zien, in menuvolgorde. */
function navFor(access: Access) {
  const visibleNav = adminNav.filter(n => canSee(n, access));
  /** In het menu: zonder wat (nog) niet in het menu hoort (`hidden`); het adres blijft werken. */
  const menuNav = visibleNav.filter(n => !n.hidden);
  /** Wat op de telefoon het vaakst nodig is (`mobile` in adminNav) — de rest zit onder "Menu". */
  // Een vaste plek onderin wijst nooit naar "Komt binnenkort": die onderdelen staan alleen in het menu.
  const fixed = menuNav.filter(n => n.mobile && !n.comingSoon);
  // Een rol zonder dagelijks salonwerk (Marketing): dan haar eerste drie onderdelen, niet alleen "Menu".
  const tabBar = fixed.length > 0 ? fixed : menuNav.filter(n => !n.comingSoon).slice(0, 3);
  const searchNav = visibleNav.filter(n => !n.comingSoon);
  return {
    visibleNav,
    menuNav,
    tabBar,
    searchNav
  };
}
type Props = {
  onLogout: () => void;
};

/**
 * Het frame van het beheer. Desktop: zijbalk en een bovenbalk met opslaan. Telefoon: een bovenbalk
 * die zegt waar je bent, de tabbalk onderin, opslaan net daarboven, en het volledige menu als
 * paneel van onderaf. De stukken staan in `shell/`.
 */
export function AdminLayout({
  onLogout
}: Props) {
  const {
    dirty,
    saving,
    save,
    discard
  } = useAdminSave();
  const {
    access
  } = useCan();
  const {
    visibleNav,
    menuNav,
    tabBar,
    searchNav
  } = useMemo(() => navFor(access), [access]);
  const [menuOpen, setMenuOpen] = useState(false);
  const {
    pathname
  } = useLocation();
  const current = sectionFor(pathname, visibleNav);
  const pageRef = useRef<HTMLDivElement>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  useEffect(() => {
    setMenuOpen(false);
    window.scrollTo({
      top: 0
    });
  }, [pathname]);

  // ⌘K / Ctrl K: zoeken in het hele beheer, vanaf elk scherm.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(v => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Het beheer-concept blijft staan bij doorklikken binnen het beheer; het register waarschuwt bij
  // het tabblad sluiten, uitloggen of een link buiten het beheer.
  useUnsavedWork(dirty, 'admin');
  const confirmLeave = useConfirmLeave();
  const counts = useNavCounts();
  const logout = () => confirmLeave('admin', onLogout);
  return <div className="admin-root min-h-screen min-h-dvh w-full overflow-x-clip bg-sand text-ink" data-id="element-5034" data-mp="1km3qu6mne8tf">
      {/* Zijbalk desktop */}
      <aside aria-label="Beheermenu" className="fixed inset-y-0 left-0 z-nav hidden w-64 flex-col border-r border-line bg-sand lg:flex" data-id="element-5035" data-mp="1fu2jmz4c78a9">
        <Link to={ADMIN_BASE} className="flex items-center gap-3 px-6 py-5" data-id="element-5036" data-mp="1aupug7pztp4i">
          <img src={LOGO} alt="Beauty Bewust" className="h-9 w-auto mix-blend-multiply" data-id="element-5037" data-mp="28d99sunvxh7y" />
          <span className="rounded-full bg-ink px-2 py-0.5 text-[11px] text-shell" data-id="element-5038" data-mp="23joexbhl93cz">Beheer</span>
        </Link>
        <AdminNavList items={menuNav} variant="sidebar" counts={counts} data-id="element-5039" data-mp="1o5b0koc9m7zi" />
        <AdminAccount onLogout={logout} data-id="element-5040" data-mp="1t32q5bpfti0j" />
      </aside>

      {/* Zoeken vindt ook wat niet in het menu staat (Mijn dag, Beveiliging), maar nooit "binnenkort". */}
      <CommandMenu open={searchOpen} onClose={() => setSearchOpen(false)} items={searchNav} pageRef={pageRef} data-id="element-5041" data-mp="1j3ln721kli89" />
      <AdminMenuSheet open={menuOpen} onClose={() => setMenuOpen(false)} items={menuNav} pageRef={pageRef} onLogout={logout} counts={counts} data-id="element-5042" data-mp="26tfxpcyf2upc" />

      <div ref={pageRef} className="lg:pl-64" data-id="element-5043" data-mp="2bs13drwfu6l6">
        {/* Bovenbalk */}
        <header className="sticky top-0 z-sticky border-b border-line bg-shell/95 backdrop-blur" data-id="element-5044" data-mp="1x34trqeqglig">
          <div className="flex h-14 items-center gap-3 px-4 sm:px-6 lg:px-10" data-id="element-5045" data-mp="221oi1xgsu9dz">
            {/* Telefoon: het logo gaat naar het overzicht, en ernaast staat waar je nu bent. */}
            <Link to={ADMIN_BASE} className="flex shrink-0 items-center lg:hidden" aria-label="Beauty Bewust beheer, naar het overzicht" data-id="element-5046" data-mp="2ujk1bkbluugc">
              <img src={LOGO} alt="" className="h-7 w-auto mix-blend-multiply" data-id="element-5047" data-mp="j2gujly9cygu" />
            </Link>
            {current && current.key !== 'overzicht' && <span className="min-w-0 truncate border-l border-line pl-3 text-[14px] text-ink lg:hidden" aria-hidden="true" data-id="element-5048" data-mp="sue6ww105wh5">
                {current.label}
              </span>}

            <div className="flex min-w-0 flex-1 items-center justify-end gap-2 text-[13px] lg:justify-start" role="status" data-id="element-5049" data-mp="4xisakf11qq">
              {dirty ? <span className="flex items-center gap-2 text-ink" data-id="element-5050" data-mp="3rdl6uya1j31z">
                  <span className="h-2 w-2 shrink-0 rounded-full bg-clay" aria-hidden="true" data-id="element-5051" data-mp="976uh3rf715p" />
                  <span className="hidden truncate lg:inline" data-id="element-5052" data-mp="53h3k7n9w7hi">Niet-opgeslagen wijzigingen · Opslaan zet ze live</span>
                  {/* Telefoon: de opslaanbalk onderin zegt het al; hier alleen het bolletje. */}
                  <span className="sr-only lg:hidden" data-id="element-5053" data-mp="1gj5sv5o01ilh">Niet-opgeslagen wijzigingen</span>
                </span> : <span className="flex items-center gap-1.5 text-muted" data-id="element-5054" data-mp="db5rgn77z173">
                  <CheckIcon className="h-4 w-4 text-sage" strokeWidth={2} aria-hidden="true" data-id="element-5055" data-mp="i6bczkwpmdun" />
                  <span className="hidden lg:inline" data-id="element-5056" data-mp="n7h7no9xkb6t">Alles is opgeslagen</span>
                  <span className="sr-only lg:hidden" data-id="element-5057" data-mp="r7j8xwbr72lk">Alles is opgeslagen</span>
                </span>}
            </div>

            <button type="button" onClick={() => setSearchOpen(true)} aria-haspopup="dialog" aria-label="Zoeken in het beheer" className="inline-flex h-10 shrink-0 items-center gap-2 rounded-full border border-line bg-white px-3 text-[13px] text-muted transition-colors duration-150 ease-soft hover:border-ink/30 hover:text-ink lg:w-56" data-id="element-5058" data-mp="3qpit508blkfg">
              <SearchIcon className="h-4 w-4" strokeWidth={1.75} aria-hidden="true" data-id="element-5059" data-mp="3bys5m4drh7i5" />
              <span className="hidden flex-1 text-left lg:inline" data-id="element-5060" data-mp="3bwp7ex2gg82c">Zoeken</span>
              <kbd className="hidden rounded border border-line px-1.5 text-[11px] lg:inline" data-id="element-5061" data-mp="34er111fkld3c">⌘K</kbd>
            </button>
            <a href={shopHref('/')} target="_blank" rel="noreferrer" className="hidden items-center gap-1.5 rounded-md px-2.5 py-1.5 text-[13px] text-muted transition-colors duration-150 ease-soft hover:text-ink lg:inline-flex" data-id="element-5062" data-mp="2zed1k7s1bbgx">
              Bekijk site
              <ExternalLinkIcon className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" data-id="element-5063" data-mp="2gpy7tcqc1jcf" />
              <span className="sr-only" data-id="element-5064" data-mp="2bqbwj8u2ed69"> (opent in een nieuw tabblad)</span>
            </a>
            <div className="hidden items-center gap-2 lg:flex" data-id="element-5065" data-mp="27p07b4f3rtyl">
              {dirty && <button type="button" onClick={discard} disabled={saving} className="rounded-full px-3 py-2 text-[13px] text-muted transition-colors duration-150 ease-soft hover:text-ink" data-id="element-5066" data-mp="22pxa265d6s16">
                  Verwerpen
                </button>}
              <button type="button" onClick={() => void save()} disabled={!dirty || saving} title="Opslaan zet je wijzigingen direct live op de website" className={`${btnPrimary} py-2`} data-id="element-5067" data-mp="3qljvt7ode4t5">
                {saving ? 'Opslaan…' : 'Opslaan'}
              </button>
            </div>
          </div>
        </header>

        <main className={`mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-10 lg:py-10 lg:pb-10 ${dirty ? 'pb-44' : 'pb-28'}`} data-id="element-5068" data-mp="37oit5tiaggsd">
          {/* Vangnet per scherm: crasht een editor, dan blijven menu en opslaan bruikbaar. Doorklikken herstelt (key). */}
          <PageErrorBoundary key={pathname} showDetails title="Dit scherm laadt even niet." homeHref={ADMIN_BASE} homeLabel="Naar het overzicht" data-id="element-5069" data-mp="2jy6b3ptbx3o2">
            {/* Elk scherm is een eigen stuk code (AdminApp); het menu blijft staan terwijl het laadt. */}
            <Suspense fallback={<div role="status" className="flex min-h-[40vh] items-center justify-center text-[14px] text-muted" data-id="element-5071" data-mp="20ce9abaheyde">
                  Scherm laden…
                </div>} data-id="element-5070" data-mp="25c8zsdqchlxx">
              <Outlet data-id="element-5072" data-mp="2aak8gl4wlqek" />
            </Suspense>
          </PageErrorBoundary>
        </main>
      </div>

      <AdminSaveBar data-id="element-5073" data-mp="2oabpyvumpoxb" />
      <AdminTabBar tabs={tabBar} current={current} menuOpen={menuOpen} dirty={dirty} counts={counts} onMenu={() => setMenuOpen(true)} data-id="element-5074" data-mp="2o9jwproi83a7" />
    </div>;
}